import api from "./apiClient";
import API_ENDPOINTS from "../config/apiEndpoints";
import {
  assertApiSuccess,
  rowsFromResponse,
  tablesFromResponse,
} from "../utils/apiResponseUtils";

const guidOrNull = (value) => {
  const text = String(value ?? "").trim();
  return text || null;
};

const pickTable = (tables, names = []) => {
  const wanted = new Set(names.map((n) => String(n).toLowerCase()));
  const match = tables.find((t) => wanted.has(String(t.name).toLowerCase()));
  return match?.rows || [];
};

function basePayload(userData, overrides = {}) {
  return {
    LocationId: null,
    LocationName: null,
    BuildingId: null,
    BuildingName: null,
    BuildingTotalNumber: null,
    FloorId: null,
    FloorName: null,
    RoomId: null,
    RoomName: null,
    CapacityCount: 0,
    RoomCapacityId: null,
    RoomCapacityName: null,
    CapacityType: null,
    IsActive: null,
    IncludeBuildings: true,
    IncludeFloors: true,
    IncludeRooms: true,
    IsBlocked: false,
    BlockedReason: null,
    BlockStartDateTime: null,
    BlockEndDateTime: null,
    OrganizationId: guidOrNull(userData?.OrganizationId),
    CreatedBy: guidOrNull(userData?.StaffId),
    ActionType: "",
    ...overrides,
  };
}

function withGuidFields(payload) {
  const next = { ...payload };
  [
    "LocationId",
    "BuildingId",
    "FloorId",
    "RoomId",
    "RoomCapacityId",
    "OrganizationId",
    "CreatedBy",
  ].forEach((key) => {
    if (key in next) next[key] = guidOrNull(next[key]);
  });
  return next;
}

function utilsIsSuccess(response) {
  try {
    assertApiSuccess(response);
    return true;
  } catch {
    return false;
  }
}

export async function capacityManagementActivity(userData, overrides = {}) {
  const response = await api.post(
    API_ENDPOINTS.CAPACITY_MANAGEMENT.ACTIVITY,
    withGuidFields(basePayload(userData, overrides)),
  );
  return response;
}

/** Org sites from Site Management (source of truth for site list). */
export async function fetchOrganizationSites(userData) {
  const response = await api.post(API_ENDPOINTS.SITE.SITE_ACTIVITY, {
    SiteId: "",
    SiteName: "",
    SiteAbbr: "",
    Location: "",
    ContactName: "",
    ContactPhone: "",
    ContactEmail: "",
    Address: "",
    Postcode: "",
    OdsCode: "",
    NhsNumber: "",
    StaffId: "",
    StudyId: "",
    OrganizationId: userData?.OrganizationId || "",
    CreatedBy: userData?.StaffId || "",
    ActionType: "GetAllSites",
  });
  if (!utilsIsSuccess(response)) return [];
  const data = response?.data;
  if (Array.isArray(data?.SuccessStory)) return data.SuccessStory;
  if (Array.isArray(data)) return data;
  return rowsFromResponse(response, ["SuccessStory", "successStory"]);
}

export async function fetchCapacityLocations(userData) {
  const response = await capacityManagementActivity(userData, {
    ActionType: "GetLocationsByOrganizationId",
  });
  const tables = tablesFromResponse(response);
  const fromTables = pickTable(tables, ["SiteDetails", "siteDetails"]);
  if (fromTables.length) return fromTables;
  return rowsFromResponse(response, ["SiteDetails", "siteDetails"]);
}

/**
 * Map a Site Management site to the capacity LocationId used by building/room APIs.
 * Prefer exact SiteId match, then name match; fall back to SiteId.
 */
export function resolveCapacityLocationId(orgSite, capacityLocations = []) {
  const siteId = String(orgSite?.SiteId || "").trim();
  const siteName = String(orgSite?.SiteName || "").trim().toLowerCase();
  const list = Array.isArray(capacityLocations) ? capacityLocations : [];

  const byId = list.find(
    (row) =>
      String(row?.LocationId || "")
        .trim()
        .toLowerCase() === siteId.toLowerCase(),
  );
  if (byId?.LocationId) return String(byId.LocationId).trim();

  const byName = list.find(
    (row) =>
      String(row?.LocationName || "")
        .trim()
        .toLowerCase() === siteName,
  );
  if (byName?.LocationId) return String(byName.LocationId).trim();

  return siteId;
}

export async function fetchCapacityBuildings(userData, locationId) {
  const response = await capacityManagementActivity(userData, {
    LocationId: locationId,
    ActionType: "GetLocationandBuildingsbyLocationId",
  });
  // Even when legacy returns success=0 for empty site header, still read BuildingDetails.
  const tables = tablesFromResponse(response);
  const buildings = pickTable(tables, ["BuildingDetails", "buildingDetails"]);
  const site = pickTable(tables, ["SiteDetails", "siteDetails"])[0] || null;
  if (buildings.length || site || utilsIsSuccess(response)) {
    return { site, buildings };
  }
  return { site: null, buildings: [] };
}

/**
 * Ensure a capacity LocationId exists for a Site Management site.
 * Sites are owned by Site Management; capacity API still needs its own Location row.
 */
export async function ensureCapacityLocationId(userData, orgSite) {
  const locations = await fetchCapacityLocations(userData);
  const existing = resolveCapacityLocationId(orgSite, locations);
  const siteId = String(orgSite?.SiteId || "").trim();
  const siteName = String(orgSite?.SiteName || "").trim();

  const matched = (locations || []).find((row) => {
    const lid = String(row?.LocationId || "")
      .trim()
      .toLowerCase();
    const lname = String(row?.LocationName || "")
      .trim()
      .toLowerCase();
    return (
      (siteId && lid === siteId.toLowerCase()) ||
      (siteName && lname === siteName.toLowerCase())
    );
  });

  if (matched?.LocationId) {
    return {
      locationId: String(matched.LocationId).trim(),
      locations,
      created: false,
    };
  }

  // No capacity location yet — create one using the Site Management name (not editable later in UI).
  if (!siteName) {
    return { locationId: existing || siteId, locations, created: false };
  }

  const response = await capacityManagementActivity(userData, {
    LocationId: null,
    LocationName: siteName,
    IncludeBuildings: true,
    IncludeFloors: true,
    IncludeRooms: true,
    IsBlocked: false,
    ActionType: "AddSite",
  });
  const row = firstSuccessRow(response);
  const createdId =
    String(row?.LocationId || "").trim() ||
    resolveCapacityLocationId(
      orgSite,
      await fetchCapacityLocations(userData),
    );

  const refreshed = await fetchCapacityLocations(userData);
  return {
    locationId: createdId,
    locations: refreshed,
    created: Boolean(createdId),
    message: row?.MSG || "",
  };
}


export async function fetchCapacityFloors(userData, locationId, buildingId) {
  const response = await capacityManagementActivity(userData, {
    LocationId: locationId,
    BuildingId: buildingId,
    ActionType: "GetFloorsByBuildingId",
  });
  const tables = tablesFromResponse(response);
  return pickTable(tables, ["FloorDetails", "floorDetails"]);
}

export async function fetchCapacityRooms(
  userData,
  locationId,
  buildingId,
  floorId,
) {
  const response = await capacityManagementActivity(userData, {
    LocationId: locationId,
    BuildingId: buildingId,
    FloorId: floorId,
    ActionType: "GetRoomsByFloorId",
  });
  const tables = tablesFromResponse(response);
  return {
    rooms: pickTable(tables, ["RoomDetails", "roomDetails"]),
    capacities: pickTable(tables, [
      "RoomCapacityDetails",
      "roomCapacityDetails",
    ]),
  };
}

export function firstSuccessRow(response) {
  const rows = rowsFromResponse(response, ["SuccessStory", "successStory"]);
  return rows[0] || null;
}

export function successRows(response) {
  return rowsFromResponse(response, ["SuccessStory", "successStory"]);
}

export function isCapacitySuccess(row) {
  if (!row) return false;
  return (
    Number(row.Success) === 1 ||
    row.Success === true ||
    row.Success === "1" ||
    // Some capacity mutations return the created entity without a Success flag.
    Boolean(String(row.BuildingId || row.FloorId || row.RoomId || row.LocationId || "").trim())
  );
}

/** Legacy block SP expects DD-MM-YYYY HH:mm:ss */
export function toLegacyBlockDateTime(dateIso, timeIso) {
  const [year, month, day] = String(dateIso || "").split("-");
  const [hour = "00", minute = "00"] = String(timeIso || "").split(":");
  if (!year || !month || !day) return "";
  return `${day.padStart(2, "0")}-${month.padStart(2, "0")}-${year} ${hour.padStart(2, "0")}:${minute.padStart(2, "0")}:00`;
}
